import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { adminApi, auth } from '@/lib/api';
import { ADMIN } from '@/constants/testIds';
import { Logo } from '@/components/site/Logo';
import { useSeo } from '@/lib/seo';

export default function AdminLoginPage() {
  useSeo({ title: 'Admin Login | A Guy Who Hunts' });
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await adminApi.login({ email, password });
      auth.setToken(res.token);
      toast.success('Welcome back.');
      nav('/admin', { replace: true });
    } catch (err) {
      const message = err?.response?.data?.detail || 'Invalid email or password.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))] px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <Logo />
        </div>
        <Card className="border-[hsl(var(--border))] card-elev">
          <CardContent className="p-6 sm:p-8">
            <p className="kicker">Admin</p>
            <h1 className="font-serif text-2xl tracking-tight mt-2">Sign in to publish.</h1>
            <form onSubmit={submit} className="mt-6 grid gap-4" noValidate>
              <div>
                <Label htmlFor="email" className="kicker mb-2 block">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid={ADMIN.emailInput}
                  autoComplete="email"
                  required
                />
              </div>
              <div>
                <Label htmlFor="password" className="kicker mb-2 block">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid={ADMIN.passwordInput}
                  autoComplete="current-password"
                  required
                />
              </div>
              {error && (
                <p data-testid={ADMIN.loginError} className="text-sm text-[hsl(var(--destructive))]" role="alert">
                  {error}
                </p>
              )}
              <Button type="submit" disabled={submitting} data-testid={ADMIN.submitButton} className="mt-2">
                {submitting ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
          </CardContent>
        </Card>
        <p className="mt-4 text-center text-xs text-[hsl(var(--muted-foreground))]">
          Public site is available without signing in.
        </p>
      </div>
    </div>
  );
}
